/* Product discovery: verified scent matching, lazy section feeds, existing theme cards. */
(() => {
  if (customElements.get('fn-product-discovery')) return;
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const values = value => {
    let parsed;
    try { parsed = JSON.parse(value || 'null'); } catch { parsed = value; }
    return [...new Set((Array.isArray(parsed) ? parsed.flat(Infinity) : String(parsed || '').split(/[,;|\n]/)).map(String).map(s => s.trim()).filter(Boolean))];
  };
  const metadata = el => ({
    id: el.dataset.id, handle: el.dataset.handle, title: el.dataset.title,
    brand: normalize(el.dataset.brand), gender: el.dataset.gender,
    verified: el.dataset.verified === 'verified',
    notes: [...new Set([...values(el.dataset.notes), ...values(el.dataset.filterNotes)].map(normalize))],
    queryNotes: values(el.dataset.filterNotes),
    families: values(el.dataset.families).map(normalize), queryFamilies: values(el.dataset.families)
  });
  const fragranceKey = item => `${item.brand}|${normalize(item.title).split(/\b(?:eau|parfum|edp|edt|cologne spray|tester|unboxed)\b/)[0].replace(/\b\d+(?: \d+)? (?:oz|ml)\b/g, '').trim()}`;
  const overlap = (a, b) => a.filter(n => b.includes(n)).length;
  const score = (current, candidate) => {
    if (!current.verified || !candidate.verified || current.id === candidate.id || fragranceKey(current) === fragranceKey(candidate)) return -1;
    if (current.gender && candidate.gender && current.gender !== 'unisex' && candidate.gender !== 'unisex' && current.gender !== candidate.gender) return -1;
    const notes = overlap(current.notes, candidate.notes);
    const families = overlap(current.families, candidate.families);
    if (notes < 2 && !(notes && families) && !families) return -1;
    return notes * 12 + (notes / Math.max(current.notes.length, candidate.notes.length, 1)) * 40 + families * 8;
  };
  const handlesIn = root => new Set([...root.querySelectorAll('a[href*="/products/"]')].map(a => a.getAttribute('href').split('/products/')[1].split(/[?#/]/)[0]));
  const feeds = new Map();
  function readFeed(url) {
    if (!feeds.has(url)) feeds.set(url, fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(12000) }).then(r => {
      if (!r.ok) throw new Error('Discovery feed unavailable');
      return r.text();
    }).then(html => [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('template[data-fn-candidate]')]));
    return feeds.get(url);
  }
  class ProductDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.current = metadata(this);
      this.list = this.querySelector('ul');
      this.panel = this.querySelector('.fn-discovery__content');
      this.pool = [...this.querySelectorAll('template[data-fn-candidate]')];
      this.querySelectorAll('[data-direction]').forEach(button => button.addEventListener('click', () => {
        this.list.scrollBy({ left: Number(button.dataset.direction) * this.list.clientWidth * .85, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }));
      this.list.addEventListener('scroll', () => this.controls(), { passive: true });
      this.resize = new ResizeObserver(() => this.controls());
      this.resize.observe(this.list);
      this.onDiscovery = () => this.render();
      document.addEventListener('fn:discovery-updated', this.onDiscovery);
      this.core = document.querySelector('product-recommendations.related-products');
      if (this.core) {
        this.coreObserver = new MutationObserver(() => this.render());
        this.coreObserver.observe(this.core, { childList: true, subtree: true });
      }
      // Keep the anchor observable even while the carousel is empty.
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(e => e.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '700px' });
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.observer?.disconnect(); this.coreObserver?.disconnect(); this.resize?.disconnect();
      document.removeEventListener('fn:discovery-updated', this.onDiscovery);
    }
    async load() {
      if (this.dataset.mode === 'similar' && this.current.verified) {
        const path = this.dataset[this.current.gender] || this.dataset.unisex;
        if (path) {
          // Mix signature notes with family matches. Filters apply to the complete collection.
          const common = new Set(['musk', 'vanilla', 'cedarwood', 'bergamot']);
          const notes = [...this.current.queryNotes].sort((a, b) => Number(common.has(normalize(a))) - Number(common.has(normalize(b)))).slice(0, 3);
          const filters = notes.map(n => ['filter.p.m.custom.filter_fragrance_notes', n]);
          if (this.current.queryFamilies[0]) filters.push(['filter.p.m.custom.filter_scent_families', this.current.queryFamilies[0]]);
          const requests = filters.map(([key, value]) => {
            const url = new URL(path, location.origin);
            url.searchParams.set(key, value); url.searchParams.set('filter.v.availability', '1');
            url.searchParams.set('sort_by', 'best-selling'); url.searchParams.set('section_id', 'fn-discovery-feed');
            return readFeed(url.href);
          });
          const results = await Promise.allSettled(requests);
          if (!this.isConnected) return;
          this.pool = results.flatMap(r => r.status === 'fulfilled' ? r.value : []);
        }
      }
      this.loaded = true;
      this.render();
    }
    render() {
      if (!this.loaded) return;
      const reserved = this.core ? handlesIn(this.core) : new Set();
      if (this.dataset.mode === 'featured') document.querySelectorAll('fn-product-discovery[data-mode="similar"] [data-discovery-handle]').forEach(el => reserved.add(el.dataset.discoveryHandle));
      const seen = new Set([this.current.handle]);
      const editions = new Set([fragranceKey(this.current)]);
      let candidates = this.pool.map(template => ({ template, item: metadata(template) }));
      if (this.dataset.mode === 'similar') candidates = candidates.map(c => ({ ...c, score: score(this.current, c.item) })).filter(c => c.score >= 0).sort((a, b) => b.score - a.score);
      const selected = [];
      const brands = new Map();
      for (const c of candidates) {
        const key = fragranceKey(c.item);
        if (reserved.has(c.item.handle) || seen.has(c.item.handle) || editions.has(key)) continue;
        if (this.dataset.mode === 'featured' && (brands.get(c.item.brand) || 0) >= 2) continue;
        brands.set(c.item.brand, (brands.get(c.item.brand) || 0) + 1);
        seen.add(c.item.handle); editions.add(key); selected.push(c);
        if (selected.length >= Number(this.dataset.limit || 8)) break;
      }
      const signature = selected.map(c => c.item.id).join(',');
      if (signature === this.signature) return;
      this.signature = signature;
      const fragment = document.createDocumentFragment();
      selected.forEach(c => fragment.append(c.template.content.cloneNode(true)));
      this.list.replaceChildren(fragment);
      this.panel.hidden = selected.length === 0;
      this.controls();
      if (this.dataset.mode === 'similar') document.dispatchEvent(new CustomEvent('fn:discovery-updated'));
    }
    controls() {
      const max = this.list.scrollWidth - this.list.clientWidth;
      const rtl = getComputedStyle(this.list).direction === 'rtl';
      const left = Math.abs(this.list.scrollLeft);
      this.querySelector('[data-direction="-1"]').disabled = rtl ? left >= max - 2 : left <= 2;
      this.querySelector('[data-direction="1"]').disabled = rtl ? left <= 2 : left >= max - 2;
    }
  }
  customElements.define('fn-product-discovery', ProductDiscovery);
})();
